import { useState, useEffect } from 'react';
import styled from 'styled-components';

import DefaultInput from 'components/input/DefaultInput';
import UploadInput from 'components/input/UploadInput';
import * as S from './Step1';
import { font } from 'asserts/styles/fonts';
import { color } from 'asserts/styles/colors';
import RadioButton from 'components/button/RadioButton';
import { certificateData, INPUT_WIDTH, SELECT_GENDER } from 'consts/join';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { QueryKeys, restfetcher } from 'queryClient';
import { useMutation, useQuery } from '@tanstack/react-query';
import DefaultSelectMenu from 'components/selectMenu/DefaultSelectMenu';
import DefaultCheckBox from 'components/checkbox/DefaultCheckBox';
import IframeViewer from 'components/layer/IframeViewer';
import { isValidPassword, validatePassword } from 'utils/validate';
import SelectData from 'components/selectMenu/SelectData';
import { formatBirthday } from 'utils/date';
import useInitUploadData from 'hooks/useInitUploadData';
import { allowOnlyNumbers, formatPhoneNumber } from 'utils/common';
import DefaultButton from '../button/DefaultButton';

interface Props {
  hasReset: boolean;
  isJoinUser: boolean;
  setHasReset: React.Dispatch<React.SetStateAction<boolean>>;
  businessNumber: BusinessNumber;
  bizFileData: FileType | undefined;
  setBizFileData: React.Dispatch<FileType | undefined>;
  setBusinessNumber: React.Dispatch<React.SetStateAction<BusinessNumber>>;
}

const { X_SMALL, SMALL, LARGE } = INPUT_WIDTH;

const Step3 = ({
  hasReset,
  isJoinUser,
  businessNumber,
  bizFileData,
  setHasReset,
  setBusinessNumber,
  setBizFileData
}: Props) => {
  const { watch, control, reset, setValue, getValues } = useFormContext<JoinTypeOnlyUsedFront>();

  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isCheckedPreRegCode, setIsCheckedPreRegCode] = useState(0);
  const [preRegCodeIdx, setPreRegCodeIdx] = useState(0);
  const [recvBusinessName, setRecvBusinessName] = useState("");
  const [recvCategoryname, setRecvCategoryname] = useState(0);
  const [sendPrecode, setSendPrecode] = useState("");


  const usrId = useWatch({ name: 'usrid', control });
  const passwordValue = useWatch({ name: 'usrpw', control });
  const passwordCheck = watch('passwordCheck');
  const contractUrl = watch('contracturl');
  const contractName = watch('contractname');
  const categoryIdx = watch('categoryidx');
  const bizlicenseUrl = watch('bizlicenseurl');
  const bizlicensename = watch('bizlicensename');
  const bizFile = watch('bizFile');
  const presignupIdx = watch('presignupidx');
  const preregcodeIdx = watch("preregcodeidx")

  const onChangeFile = (file: any) => {
    setValue('bizFile', file);
    if (file) {
      setBizFileData({
        uid: file.uid,
        name: file.name,
        status: file.status,
        percent: file.percent
      });
      setValue('bizlicensename', file.name);
    } else {
      setBizFileData(undefined);
      setValue('bizlicensename', '');
    }
  };

  useInitUploadData({
    file: bizFile,
    fallbackUrl: bizlicenseUrl,
    fallbackName: bizlicensename,
    setState: setBizFileData
  });

  const isTouched = !!passwordCheck;

  const requestPreSignUpData = useQuery({
    queryKey: [QueryKeys.PRESIGN_UP_DATA, usrId],
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/auth/find/info/byusrid',
        params: {
          usrid: usrId
        }
      }),
    enabled: !!usrId
  });

  const { data: preSignUpData } = requestPreSignUpData;

  const handleClickOpenViewer = () => {
    setIsViewerOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleClickCloseViewer = () => {
    setIsViewerOpen(false);
    document.body.style.overflow = 'visible';
  };


  const handleClickCheckPreRegCode = () => {
    requestSavePreRegCode.mutate();
  }

  const requestSavePreRegCode = useMutation({
    mutationFn: () =>
      restfetcher({
        method: "POST",
        path: "api/v1/pre-regcode/check/precode",
        params: {
          precode : sendPrecode,
        }
      }),
    onSuccess: (data: any) => {
      if (data?.result === "success") {
        setIsCheckedPreRegCode(1);
        setPreRegCodeIdx(data?.data.preregcodeidx);
        setRecvBusinessName(data?.data?.businessname);
        setRecvCategoryname(data?.data?.categoryname);
        setValue('preregcodeidx', data?.data.preregcodeidx);
      } else if (data?.result === "failed") {
        setIsCheckedPreRegCode(2);
        setPreRegCodeIdx(0);
      } else {
        setIsCheckedPreRegCode(0);
      }
    },
  });

  const requestCategoryList = useQuery({
    queryKey: [QueryKeys.CATEGORYTYPE],
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/auth/category',
        params: {}
      }),
    enabled: !!usrId
  });

  const categoryList = requestCategoryList?.data?.category;

  const categoryOptions: CategoryList[] = [
    ...(categoryList?.filter((item: CategoryList) => item.categoryidx !== 0) || [])
  ];

  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const { value, name } = e.currentTarget;

    if (name.includes('businessNumber')) {
      setBusinessNumber({
        ...businessNumber,
        [name]: value.replace(/[^0-9]/g, '')
      });
    }
  };

  useEffect(() => {
    const shouldReset = preSignUpData?.result === 'success' && !hasReset;

    if (shouldReset) {
      const { usridx, presignupidx, userInfo, bizInfo } = preSignUpData;

      const currentValues = getValues();

      const mappedData = {
        ...currentValues,
        usridx: usridx ?? 0,
        presignupidx: presignupidx ?? 0,
        nationcodeidx: userInfo?.nationcodeidx ?? 0,
        usrphoneregioncodeidx: userInfo?.usrphoneregioncodeidx ?? 0,
        usrphoneno: userInfo?.usrphoneno ?? '',
        usrbirth: userInfo?.usrbirth ?? '',
        usrname: userInfo?.usrname ?? '',
        usrgender: userInfo?.usrgender === 0 ? 1 : userInfo?.usrgender ?? 1,
        bizcerttype: bizInfo?.bizcerttype ?? 0,
        zipcode: bizInfo?.zipcode ?? '',
        number: bizInfo?.number ?? '',
        contracturl: bizInfo?.contracturl ?? '',
        address: bizInfo?.address ?? '',
        companyname: bizInfo?.companyname ?? '',
        contractname: bizInfo?.contractname ?? '',
        categoryidx: bizInfo?.categoryidx ?? 0,
        preregcodeidx: preRegCodeIdx ?? 0
      };

      if (bizInfo?.bizlicensenum) {
        const [part1 = '', part2 = '', part3 = ''] = bizInfo.bizlicensenum.split('-');
        setBusinessNumber({
          businessNumber1: part1,
          businessNumber2: part2,
          businessNumber3: part3
        });
      }

      if (bizInfo.bizlicensename) {
        const dummyFile = {
          uid: '',
          name: bizInfo.bizlicensename,
          status: '',
          percent: ''
        };
        setBizFileData(dummyFile);

        setValue('bizFile', dummyFile);
        setValue('bizlicensename', bizInfo?.bizlicensename);
      }

      reset(mappedData);
      setHasReset(true); // step 이동시 초기화 방지
    }
  }, [preSignUpData, usrId]);

  return (
    <Wrapper>
      <Ul>
        <Li>
          <Label>* 이름</Label>
          <Controller
            control={control}
            name="usrname"
            rules={{ required: '필수 값을 입력해주세요.' }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <DefaultInput
                  name="usrname"
                  value={value}
                  onChange={onChange}
                  width={SMALL}
                  status={error ? 'error' : undefined}
                  statusText={error?.message}
                />
              );
            }}
          />
        </Li>
        <Li>
          <Label>* 생년월일</Label>
          <Controller
            control={control}
            name="usrbirth"
            rules={{ required: '필수 값을 입력해주세요.' }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const handleChangeBirthDay = (e: React.ChangeEvent<HTMLInputElement>) => {
                const formatted = formatBirthday(e.target.value);
                onChange(formatted);
              };

              return (
                <DefaultInput
                  name="usrbirth"
                  value={value ?? '-'}
                  onChange={handleChangeBirthDay}
                  width={SMALL}
                  placeholder="YYYY-MM-DD"
                  status={error ? 'error' : undefined}
                  statusText={error?.message}
                />
              );
            }}
          />
        </Li>
        <Li>
          <Label>휴대폰 번호</Label>
          <Controller
            control={control}
            name="usrphoneno"
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <DefaultInput
                  value={value}
                  name="usrphoneno"
                  onChange={(e) => {
                    const input = e.target.value;
                    onChange(formatPhoneNumber(input));
                  }}
                  width={SMALL}
                  status={error ? 'error' : undefined}
                  statusText={error?.message}
                />
              );
            }}
          />
        </Li>
        <Li>
          <Label>* 성별</Label>
          <Controller
            control={control}
            name="usrgender"
            render={({ field: { onChange, value } }) => {
              return (
                <div>
                  <RadioButton value={value} options={SELECT_GENDER} onChange={onChange} />
                </div>
              );
            }}
          />
        </Li>
        <Li>
          <Label>이메일</Label>
          <Controller
            control={control}
            name="usrid"
            render={({ field: { value } }) => (
              <>
                <div className="isBizData">{value}</div>
              </>
            )}
          />
        </Li>
        {!isJoinUser && (
          <Li className="password">
            <Label>* 비밀번호</Label>
            <div>
              <div>
                <Controller
                  control={control}
                  name="usrpw"
                  rules={{
                    required: '비밀번호 입력',
                    validate: validatePassword()
                  }}
                  render={({ field, fieldState }) => {
                    return (
                      <DefaultInput
                        {...field}
                        password
                        name="usrpw"
                        placeholder="비밀번호 입력"
                        width={SMALL}
                        status={fieldState.error ? 'error' : undefined}
                        statusText={fieldState.error?.message}
                      />
                    );
                  }}
                />
                <Controller
                  control={control}
                  name="passwordCheck"
                  rules={{
                    required: '비밀번호 재입력',
                    validate: (value) => {
                      return value === watch('usrpw') || '일치하지 않습니다.';
                    }
                  }}
                  render={({ field, fieldState }) => {
                    const showSuccess =
                      isValidPassword(passwordValue) &&
                      field.value === passwordValue &&
                      field.value !== '';

                    return (
                      <DefaultInput
                        {...field}
                        password
                        name="passwordCheck"
                        placeholder="비밀번호 재입력"
                        width={SMALL}
                        disabled={!isValidPassword(passwordValue)}
                        status={
                          !isTouched
                            ? undefined
                            : fieldState.invalid
                            ? 'error'
                            : showSuccess
                            ? 'success'
                            : undefined
                        }
                        statusText={
                          !isTouched
                            ? ''
                            : fieldState.invalid
                            ? fieldState.error?.message ||
                              (field.value === '' ? '비밀번호 재입력' : '')
                            : showSuccess
                            ? '등록 가능합니다.'
                            : ''
                        }
                      />
                    );
                  }}
                />
              </div>
              <InfoText className="text">
                *문자, 숫자, 특수문자 중 2개 이상 조합, 10글자 이상
              </InfoText>
            </div>
          </Li>
        )}
      </Ul>
      <InfoText>
        *기존에 입력된 회원정보 변경이 필요하신 엑스바디앱 또는
        통합웹사이트(https://member.exbody.com)를 이용해주세요.
      </InfoText>
      <S.Line />
      {/* 비즈 */}
      <Ul>
        <h3>비즈니스 정보를 입력해주세요.</h3>
        <Li>
          <Label>사전코드</Label>
          <div>
            <ArrangeHoriCtrl>
              {/*<InputLayout>*/}
                <DefaultInput
                  name="preCode"
                  value={sendPrecode}
                  onChange={(e) => setSendPrecode(e.target.value)}
                  width={LARGE}
                />
              {/*</InputLayout>*/}
              <ButtonWrapper>
                <DefaultButton onClick={handleClickCheckPreRegCode} text="확인" size="small" />
              </ButtonWrapper>
              {isCheckedPreRegCode === 1 && (<Success>사용 가능한 코드입니다.</Success>)}
              {isCheckedPreRegCode === 2 && (<Warning>사전코드를 확인해주세요.</Warning>)}
            </ArrangeHoriCtrl>
            <InfoText className="text">
                * 사전코드가 있을 경우에만 입력해주세요.
            </InfoText>
          </div>
        </Li>
        {/*<Li>
          <InfoText className="text">
                * 사전코드가 있을 경우에만 입력해주세요.
          </InfoText>
        </Li>*/}
        {isCheckedPreRegCode === 1 && (<Li>
          <Label>계약종류</Label>
          <BusinessInfo>{recvBusinessName}</BusinessInfo>
        </Li>)}
        <Li className="select">
          <Label>* 비즈니스 카테고리</Label>
          {preRegCodeIdx <= 0 && (<Controller
            control={control}
            name="categoryidx"
            rules={{
              required: '필수 값을 입력해주세요.',
              validate: (value) => {
                return value !== 0;
              }
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return presignupIdx !== 0 ? (
                <SelectData
                  data={categoryIdx}
                  option={categoryOptions.map((item) => ({
                    value: item.categoryidx,
                    label: item.categoryname
                  }))}
                />
              ) : (
                <SelectWrapper
                  data-error-name="categoryidx"
                  className={`category ${error ? 'error' : ''}`}
                >
                  <DefaultSelectMenu
                    options={categoryOptions.map((item) => ({
                      value: item.categoryidx,
                      label: item.categoryname
                    }))}
                    size="large"
                    value={value || '카테고리 선택'}
                    onChange={onChange}
                    placeholder="선택"
                  />
                </SelectWrapper>
              );
            }}
          />)}
          {preRegCodeIdx > 0 && (
            <BusinessInfo>{recvCategoryname}</BusinessInfo>
          )}
        </Li>
        <Li>
          <Label>* 상호명</Label>
          <Controller
            control={control}
            name="companyname"
            rules={{ required: '필수 값을 입력해주세요.' }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <DefaultInput
                  name="companyname"
                  value={value}
                  onChange={onChange}
                  width={LARGE}
                  status={error ? 'error' : undefined}
                  statusText={error?.message}
                />
              );
            }}
          />
        </Li>
        <Li>
          <Label>사업장 주소</Label>
          <Controller
            control={control}
            name="address"
            render={({ field: { onChange, value } }) => (
              <DefaultInput name="address" value={value} onChange={onChange} width={LARGE} />
            )}
          />
        </Li>
        <Li>
          <Label>사업장 우편번호</Label>
          <Controller
            control={control}
            name="zipcode"
            render={({ field: { onChange, value } }) => (
              <DefaultInput
                name="zipcode"
                value={value}
                onChange={(e) => {
                  const onlyNums = allowOnlyNumbers(e.target.value);
                  onChange(onlyNums);
                }}
                width={LARGE}
              />
            )}
          />
        </Li>
        <Li>
          <Label>사업장 전화번호</Label>
          <Controller
            control={control}
            name="bizlicensename"
            defaultValue=""
            render={({ field }) => <input type="hidden" {...field} />}
          />
          <Controller
            control={control}
            name="number"
            render={({ field: { onChange, value } }) => (
              <DefaultInput
                name="number"
                value={value?.toString()}
                onChange={(e) => {
                  const onlyNums = allowOnlyNumbers(e.target.value);
                  onChange(onlyNums);
                }}
                width={LARGE}
              />
            )}
          />
        </Li>
        <Li>
          <Label>등록증</Label>
          <CertifiCateWrapper>
            <Controller
              control={control}
              name="bizcerttype"
              render={({ field: { onChange, value } }) => {
                return <RadioButton value={value} options={certificateData} onChange={onChange} />;
              }}
            />
            <UploadInput onChangeFile={onChangeFile} file={bizFileData} isRemove={true} />
            <div className="input">
              <DefaultInput
                width={X_SMALL}
                name="businessNumber1"
                value={businessNumber.businessNumber1}
                onChange={handleChange}
                placeholder="3자리"
                maxLength={3}
              />
              -
              <DefaultInput
                width={X_SMALL}
                name="businessNumber2"
                value={businessNumber.businessNumber2}
                onChange={handleChange}
                placeholder="2자리"
                maxLength={2}
              />
              -
              <DefaultInput
                width={X_SMALL}
                name="businessNumber3"
                value={businessNumber.businessNumber3}
                onChange={handleChange}
                placeholder="5자리"
                maxLength={5}
              />
            </div>
          </CertifiCateWrapper>
        </Li>
      </Ul>
      <S.Line />
      {contractUrl && (
        <>
          <h3>* 계약서 파일 다운로드 후 계약 내용을 확인해주세요.</h3>
          <Ul>
            <Li>
              <Label>계약서</Label>
              <div style={{ cursor: 'pointer' }} onClick={handleClickOpenViewer}>
                {contractName}
              </div>
              {isViewerOpen && (
                <IframeViewer onClose={handleClickCloseViewer} selectedItem={contractUrl} />
              )}
            </Li>
            <Li>
              <Label>* 동의</Label>
              <Controller
                control={control}
                name="isContractAgreed"
                rules={{
                  validate: (value) => {
                    if (contractUrl && !value) {
                      return '계약서를 확인하고 체크해주세요.';
                    }
                    return true;
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <BasicFlex>
                    <DefaultCheckBox
                      name="isContractAgreed"
                      onChange={onChange}
                      checked={Boolean(value)}
                    />
                    {error?.message && (
                      <strong className="error">
                        {/* <BlockIcon /> */}
                        {error?.message}
                      </strong>
                    )}

                    <p>계약서에 명시된 내용에 대해 충분이 이해하였으며, 계약 내용에 동의합니다.</p>
                  </BasicFlex>
                )}
              />
            </Li>
          </Ul>
          <InfoText>*계약서의 비밀번호는 계약담당자에게 문의해주세요.</InfoText>
          <S.Line />
        </>
      )}
    </Wrapper>
  );
};

export default Step3;

const Wrapper = styled.div`
  color: ${color.black2};
`;

const Ul = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 32px;
`;

const Li = styled.li`
  display: flex;
  align-items: center;
  gap: 16px;

  &.password {
    & > div > div {
      display: flex;
      gap: 8px;

      &.text {
        margin-top: 32px;
      }
    }
  }

  &.select {
    .ant-select {
      width: 240px;
      height: 36px;
    }
  }

  & .radio-group-error {
    border: 1px solid red;
    border-radius: 4px;
  }

  & .ant-radio-group {
    padding: 10.5px;
  }

  & div.parentData {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  & div.isBizData {
    height: 44px;
    line-height: 44px;

    &.gap {
      display: flex;
      gap: 8px;
    }
  }
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  color: ${color.black2};
  width: 160px;
  gap: 3px;

  ${font.body_regular}

  span {
    color: ${color.red};
  }
`;

const CertifiCateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  & div.ant-radio-group {
    padding: 0;
  }

  & div.input {
    display: flex;
    align-items: center;
    gap: 8px;

    & .ant-input-affix-wrapper {
      width: 130px;
    }
  }
`;

const InfoText = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  color: ${color.gray700};
  ${font.caption1_regular}

  & span:not(:first-of-type) {
    margin-left: 8px;
  }
`;

const BasicFlex = styled.div`
  display: flex;
  align-items: center;
  position: relative;

  & strong.error {
    display: flex;
    gap: 4px;
    position: absolute;
    bottom: -20px;
    color: ${color.red};

    ${font.caption3_regular}
  }
`;

const SelectWrapper = styled.div`
  &.category.error .ant-select-selector {
    border: 1px solid red;
  }
`;

const ButtonWrapper = styled.div`
  display: inline-block;
  background: ${color.black2};
  border-radius: 24px;
  justify-content: flex-end;
  gap: 14px;
  .ant-btn {
    width: 70px;
    height: 200px;  
  }
`;

const Warning = styled.p`
  color: ${"#ff3737"};
`;

const Success = styled.p`
  color: ${"#10db9e"};
`;

const BusinessInfo = styled.p`
  color: ${"#264a9f"};
`;

const InputLayout = styled.div`
  display: flex;
  gap: 8px;

  & span.ant-input-affix-wrapper {
    width: 100%;
  }
`;

const ArrangeHoriCtrl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;